import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup} from '@angular/forms';

import {BEAR_ANIM, SearchParam} from '../../defines/client.const';
import {NormalizeSearchKeyword} from '../../defines/client.func';
import {IsEmptyString, IsEqualObj, SafeClearTimeout} from '../../defines/common.func';
import {REGION} from "../../defines/gukeo.const";
import {GetRegionText} from '../../defines/desktop.const';
import {SearchHistory} from '../../models/history.model';

import {OptionService} from '../../services/option/option.service';
import {BearService} from '../../services/bear/bear.service';
import {LIST_HELP_FLAG, ListHelpService} from '../../services/list-help/list-help.service';
import {GlobalEventService} from "../../services/global-event/global-event.service";


const AUTO_SEARCH_DELAY = 2000;     // 2초; 단어 검색 상자에 입력하고 나서 지연시간 동안 입력이 없으면 자동으로 검색한다.


@Component({
  selector: 'app-search-box',
  template: `
    <div class="search-head-box">
      <div class="search-recently-box">
<!--           *ngIf="showGoHistory || showHistotyKeyword">-->

        <!-- div class="label-box">최근 검색 내역</div -->
        <div class="scroll-content scroll-effect over-x">
          <div class="list-box"
               *ngIf="showHistoryKeyword">


            <div class="item-box arrow"
                 [style.visibility]="historyKeyword?.list?.length === 0 ? 'hidden' : 'visible'">
              <div class="keyword-box">검색 이력
              </div>
              <a class="keyword-arrow"></a>
            </div>

            <div class="item-box"
                 *ngFor="let item of historyKeyword?.list; let idx = index"
                 (click)="onHistoryClick($event, item)">
              <div class="keyword-box">{{ item }}</div>
              <a class="keyword-remove-btn"
                 (click)="onHistoryDel($event, idx)"></a>
            </div>

          </div>
        </div>
      </div>

      <article class="total-search-form">
        <div class="search-box tiger {{ cssBear }} {{ top ? 'top' : '' }}">
          <form [formGroup]="searchForm">
            <input #search_box
                   type="text"
                   formControlName="keyword"
                   [placeholder]="GetPlaceholder()"
                   (input)="onInputSearchBox($event)"
                   (keyup)="onKeyUpSearchBox($event, search_box.value)">
          </form>
          <a class="clear-btn"
             *ngIf="search_box.value.length > 0"
             (click)="onPushClearBtn($event, search_box)"></a>
        </div>
      </article>

    </div>
  `,
})
export class SearchBoxComponent implements OnInit, OnDestroy {
  @Output() search: EventEmitter<SearchParam>;
  @Output() goHistory: EventEmitter<any>;
  @Input() showHistoryKeyword = true;
  @Input() keyHistoryKeyword: string | null = null;
  // @Input() canGoHistory: boolean = false; // 이력 화면으로 이동할 수 있는지 여부
  @Input() keyHistoryView: string | null = null;
  @Input() placeholder: string | null = null;
  @Input() selectedRegion: REGION | null = null;
  @Input() top = true; // 검색창이 상단에 위치하는지 여부

  /**
   * 현재 선택된 값. 검색 후 현재 검색된 값을 표시
   */
  @Input() set keyword(value: string | null) {
    // console.log('SearchBoxComponent::set keyword()', value, this.ctlKeyword);
    this.ctlKeyword.setValue(value === null ? null : this.normalizeKeyword(value));

    if (value === null) {
      // 안하면 이전 검색한 단어가 검색 안 된다.
      this.lastParam = null;
    }
  }

  // 입력 창 관련
  public searchForm: FormGroup = this.fb.group({
    keyword: [null],
  });

  private get ctlKeyword(): AbstractControl {
    return this.searchForm.controls['keyword'];
  }

  private lastParam: SearchParam | null = null;  // 같은 값 중복 호출 방지

  private handleAutoSearch: NodeJS.Timeout | null = null;

  /**
   * 검색어 이력
   */
  public historyKeyword: SearchHistory | null = null;

  /**
   * 검색 id 이력 - 현재 사용되지 않음.
   */
  public historyView: SearchHistory | null = null;


  constructor(
    private elementRef: ElementRef,
    private fb: FormBuilder,
    private option: OptionService,
    private listHelpSvc: ListHelpService,
    private bearSvc: BearService,
    private globalEventSvc: GlobalEventService,
  ) {
    this.search = new EventEmitter<SearchParam>();
    this.goHistory = new EventEmitter<any>();

  }


  ngOnInit(): void {
    if (!IsEmptyString(this.keyHistoryKeyword)) {
      this.historyKeyword = new SearchHistory({
        name: this.keyHistoryKeyword,
      });
    }

    if (!IsEmptyString(this.keyHistoryView)) {
      this.historyView = new SearchHistory({
        name: this.keyHistoryView,
      });
    }
  }

  ngOnDestroy(): void {
    SafeClearTimeout(this.handleAutoSearch);
    this.handleAutoSearch = null;
    this.bearSvc.reset();
  }


  public get offsetHeight(): number {
    return this.elementRef.nativeElement.offsetHeight;
  }

  /**
   * 검색 id 이력 - 현재 사용되지 않음.
   * @protected
   */
  protected get showGoHistory(): boolean {
    return !(this.historyView?.empty() ?? true);
  }

  protected get showHistotyKeyword(): boolean {
    return !(this.historyKeyword?.empty() ?? true);
  }

  /****************************************************************************
   * 검색 처리
   ****************************************************************************/

  public EmitSearch(param: SearchParam): void {
    const normalizedParam = this.normalizeSearchParam(param);

    // 자동 검색 대기중이라면 지운다.
    SafeClearTimeout(this.handleAutoSearch);
    this.handleAutoSearch = null;
    this.bearSvc.typing = false;


    if (!this.lastParam || !IsEqualObj(normalizedParam, this.lastParam)) {
      console.log('SearchBoxComponent::EmitSearch()', normalizedParam, this.lastParam);
      this.lastParam = {...normalizedParam};  // 주의: 작업 도중에 param이 변경된다. 그래서 복사해서 보관해놓는다.
      this.search.emit(normalizedParam);

      if (normalizedParam?.keyword) {
        this.historyKeyword?.addItem(normalizedParam.keyword);
        this.listHelpSvc.SetFlag(LIST_HELP_FLAG.SEARCH);
      }
    }
  }


  /**
   * 검색 박스에 입력값이 들어오면 (값이 변하면) 호출된다.
   */
  public onInputSearchBox($event: Event): void {
    const tar: any = $event.target || $event.srcElement || $event.currentTarget;
    // console.log(`SearchBoxComponent::onInputSearchBox() = "${tar.value}"`, $event);

    if (this.handleAutoSearch !== null) {
      clearTimeout(this.handleAutoSearch);
      this.handleAutoSearch = null;
    }

    if (!IsEmptyString(tar.value)) {
      this.handleAutoSearch = setTimeout(() => {
        // this.curJasoIdx = null;
        this.EmitSearch({keyword: tar.value});
        this.bearSvc.typing = false;
      }, AUTO_SEARCH_DELAY);

      this.bearSvc.typing = true;
    } else {
      this.bearSvc.typing = false;
    }
  }


  /**
   * 검색 박스에 입력되는 키 값을 확인해서 Enter면 검색을 시도한다.
   */
  public onKeyUpSearchBox($event: KeyboardEvent, keyword: string): void {
    // console.log('SearchBoxComponent::onKeyUpSearchBox()', $event.key, $event.keyCode, $event);

    if (($event.key === 'Enter' || $event.keyCode === 13) && !IsEmptyString(keyword)) {
      // this.curJasoIdx = null;
      this.EmitSearch({keyword});
    }
  }

  /**
   * 단어 검색창의 지우기 버튼 처리 함수. 검색창에 입력된 내용을 지운다.
   */
  public onPushClearBtn($event: MouseEvent, elInput: HTMLInputElement): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    // console.log('SearchBoxComponent::onPushClearBtn()', $event, elInput);
    elInput.value = '';

    this.EmitSearch({});  // 검색을 지워도 외부에서 목록이 다시 로딩되어야 해서 추가.
  }

  /**
   * HTML 쪽에서 주석으로 막음. 현재 자동으로 처리되므로 click 처리 함수 불필요.
   */
  public onClickReload(): void {
    // console.log('onClickReload()');
    // this.loadWordList();
  }

  /****************************************************************************
   * 검색창에 사투리 지역 반영.
   ****************************************************************************/
  public GetPlaceholder(): string {
    let placeholder = this.placeholder ? this.placeholder : '검색어를 입력 해 보시오.';
    if (placeholder && this.selectedRegion) {
      // console.log('GetPlaceholder()', placeholder, this.selectedRegion);
      placeholder = GetRegionText(this.selectedRegion) + ' 지역에서 ' + placeholder;
    }
    return placeholder;
  }

  /****************************************************************************
   * 검색 이력
   ****************************************************************************/
  onHistoryClick($event: MouseEvent, keyword: string): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    // this.curJasoIdx = null;
    const normalizedKeyword = this.normalizeKeyword(keyword);
    this.ctlKeyword.setValue(normalizedKeyword);
    this.EmitSearch({keyword: normalizedKeyword});
  }

  onHistoryDel($event: MouseEvent, index: number): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.historyKeyword?.removeFromIndex(index);
  }

  onClickGoHistory($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.lastParam = null;
    this.goHistory.emit();
  }

  /***************************************************************************
   * 곰돌이 애니메이션
   ***************************************************************************/
  public get cssBear(): string {
    const bearAni = this.bearSvc.numAni;
    return bearAni === BEAR_ANIM.NORMAL ? '' : 'ani' + bearAni;
  }

  private normalizeSearchParam(param: SearchParam): SearchParam {
    if (!Object.prototype.hasOwnProperty.call(param, 'keyword')) {
      return param;
    }

    const normalizedParam: SearchParam = {...param};
    const keyword = this.normalizeKeyword(normalizedParam.keyword);

    if (IsEmptyString(keyword)) {
      delete normalizedParam.keyword;
    } else {
      normalizedParam.keyword = keyword;
    }

    return normalizedParam;
  }

  private normalizeKeyword(keyword: string | null | undefined): string {
    return NormalizeSearchKeyword(keyword);
  }
}
